import styled from "@emotion/styled";
import { useSetAtom } from "jotai";
import type { ReactElement, ReactNode } from "react";
import { useCallback, useLayoutEffect, useRef, useState } from "react";

import { rgba } from "@skilitics/designer-theme";
import { getScrollableParent, scrollElementIntoView } from "@skilitics/designer-ui-components";

import { canvasHoveredIdAtom, useIsNodeHovered } from "../canvas/canvasAtoms";
import { LayerChildLayers } from "./LayerChildLayers";
import { LayerItem } from "./LayerItem";
import { LayerSortable } from "./LayerSortable";

interface Props {
  ancestorIds: number[];
  children?: ReactElement<any>[];
  hidden?: boolean;
  hiddenReason?: string;
  icon?: ReactNode;
  id: number;
  index: number;
  locked?: boolean;
  selected?: boolean;
  title?: string;
  typeId: string;
  childNodesSortableIds?: string[];

  onSelected?: () => void;
  onTitleChange?: (newTitle: string) => void;
  onToggleHidden?: () => void;
  onToggleLocked?: () => void;
}

export const LayerNodeItem = ({
  ancestorIds,
  children,
  title,
  hidden,
  hiddenReason,
  icon,
  id,
  index,
  locked,
  onSelected,
  onTitleChange,
  onToggleHidden,
  onToggleLocked,
  selected,
  typeId,
  childNodesSortableIds,
}: Props) => {
  const setHoveredId = useSetAtom(canvasHoveredIdAtom);
  const isHovered = useIsNodeHovered(id);
  const [isDropInsideEnabled, setIsDropInsideEnabled] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const layerContainerRef = useRef<HTMLDivElement>(null);

  const handleDraggingChange = useCallback(
    (isDragging: boolean) => {
      if (expanded && isDragging) {
        setExpanded(false);
      }
    },
    [expanded],
  );

  const handleMouseEnter = useCallback(() => {
    setHoveredId(id);
  }, [id, setHoveredId]);

  const handleMouseLeave = useCallback(() => {
    setHoveredId(undefined);
  }, [setHoveredId]);

  useLayoutEffect(() => {
    if (selected) {
      scrollElementIntoView(layerContainerRef.current, getScrollableParent(layerContainerRef.current));
    }
  }, [selected]);

  return (
    <LayerContainer ref={layerContainerRef} $hovered={isHovered} $hidden={hidden}>
      <LayerSortable
        ancestorIds={ancestorIds}
        hasChildLayers={!!children?.length}
        id={id}
        index={index}
        title={title}
        typeId={typeId}
        setIsDropInsideEnabled={setIsDropInsideEnabled}
        onDraggingChange={handleDraggingChange}
      >
        <LayerItem
          itemId={id}
          active={isDropInsideEnabled}
          depth={ancestorIds.length}
          expanded={expanded}
          hasChildren={children && !!children.length}
          hidden={hidden}
          hiddenReason={hiddenReason}
          hovered={isHovered}
          icon={icon}
          locked={locked}
          onSelected={onSelected}
          onTitleChange={onTitleChange}
          selected={selected}
          title={title}
          toggleExpanded={() => setExpanded(!expanded)}
          toggleHidden={onToggleHidden}
          toggleLocked={onToggleLocked}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      </LayerSortable>
      {!!childNodesSortableIds?.length && !!children?.length && expanded && (
        <LayerChildLayers id={id} childNodesSortableIds={childNodesSortableIds}>
          {children}
        </LayerChildLayers>
      )}
    </LayerContainer>
  );
};

const LayerContainer = styled.div<{
  $hovered?: boolean;
  $hidden?: boolean;
}>`
  background: ${({ $hovered, theme }) => ($hovered ? rgba(theme.surfaceVariant, 0.5) : "transparent")};
  opacity: ${({ $hidden }) => ($hidden ? 0.5 : 1)};
  display: block;
  transition: all 0.2s ease-in-out;
`;
